import { Fragment, useState, useCallback, useEffect } from 'react';
import { Modal } from 'react-bootstrap-v5';
import Swal from 'sweetalert2';
import moment from 'moment';
import FgtsConciliacaoService, { IFgtsRelatorioImportacao, IFgtsConciliacaoDecisao, IFgtsAuditoriaEvento } from '../../services/FgtsConciliacaoService';

import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

interface Props {
    show: boolean;
    onHide: () => void;
    esteiraId: number;
}

const STATUS_COR: Record<string, string> = {
    PENDENTE: '#f0ad4e',
    RECOLHIDODATA: '#28a745',
    RECOLHIDOMAIOR: '#0d6efd',
    RECOLHIDOMENOR: '#dc3545',
};

const FAIXA_SCORE_COR: Record<string, string> = {
    MATCH_FORTE: 'bg-success',
    MATCH_PROVAVEL: 'bg-primary',
    MATCH_FRACO: 'bg-warning text-dark',
    NAO_CONCILIAR_AUTOMATICAMENTE: 'bg-danger',
};

const MODALIDADE_LABEL: Record<string, string> = {
    '1_1': '1:1',
    'N_1': 'N:1',
    '1_N': '1:N',
    'N_N': 'N:N',
};

const EVENTO_ICONE: Record<string, string> = {
    GRUPO_FORMADO: 'fa-link',
    COMBINACAO_AMBIGUA_DESCARTADA: 'fa-triangle-exclamation',
    CONTA_CORRELATA_INVESTIGADA: 'fa-magnifying-glass',
    POSSIVEL_LANCAMENTO_INDEVIDO: 'fa-circle-exclamation',
    REFERENCIAL_NAO_CONFIRMADO: 'fa-question',
};

interface IScoreComponente {
    label: string;
    valor: number;
    max: number;
}

const formatVal = (v: number) => v.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });
const formatPct = (v: number | null) => v == null ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(2)}%`;

const formatValorDetalhe = (v: unknown): string => {
    if (Array.isArray(v)) return `[${v.join(', ')}]`;
    return String(v);
};

const formatDetalhe = (detalhe: string): string => {
    try {
        const obj = JSON.parse(detalhe);
        return Object.entries(obj)
            .filter(([, v]) => v !== null && v !== undefined)
            .map(([k, v]) => `${k}: ${formatValorDetalhe(v)}`)
            .join(' · ');
    } catch {
        return detalhe;
    }
};

const componentesScore = (d: IFgtsConciliacaoDecisao): IScoreComponente[] => [
    { label: 'Valor', valor: d.scoreValor, max: 50 },
    { label: 'Temporal', valor: d.scoreTemporal, max: 25 },
    { label: 'Histórico', valor: d.scoreHistorico, max: 15 },
    { label: 'Competência', valor: d.scoreCompetencia, max: 7 },
    { label: 'Documento', valor: d.scoreDocumento, max: 3 },
];

const ModalFgtsAnalise = ({ show, onHide, esteiraId }: Props) => {
    const { t } = useTranslation();
    const [aba, setAba] = useState<'decisoes' | 'sugestoes' | 'eventos'>('decisoes');
    const [loading, setLoading] = useState(false);
    const [relatorio, setRelatorio] = useState<IFgtsRelatorioImportacao | null>(null);
    const [decisoes, setDecisoes] = useState<IFgtsConciliacaoDecisao[]>([]);
    const [eventos, setEventos] = useState<IFgtsAuditoriaEvento[]>([]);
    const [decisaoExpandida, setDecisaoExpandida] = useState<number | null>(null);
    const [processandoChave, setProcessandoChave] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!esteiraId) return;
        setLoading(true);
        try {
            const [rel, dec, ev] = await Promise.all([
                FgtsConciliacaoService.buscarRelatorio(esteiraId),
                FgtsConciliacaoService.listarDecisoes(esteiraId),
                FgtsConciliacaoService.listarAuditoria(esteiraId),
            ]);
            setRelatorio(rel);
            setDecisoes(dec);
            setEventos(ev);
        } finally {
            setLoading(false);
        }
    }, [esteiraId]);

    useEffect(() => {
        if (show) {
            setAba('decisoes');
            load();
        }
    }, [show, load]);

    const totalPorStatusMax = relatorio ? Math.max(...relatorio.porStatus.map(s => s.quantidade), 1) : 1;
    const totalPorModalidadeMax = relatorio ? Math.max(...relatorio.porModalidade.map(m => m.quantidade), 1) : 1;

    const handleAceitar = async (chave: string) => {
        setProcessandoChave(chave);
        try {
            await FgtsConciliacaoService.aceitarSugestao(esteiraId, chave);
            Swal.fire({ text: t('Sugestão confirmada!'), icon: 'success', timer: 2000, showConfirmButton: false });
            await load();
        } catch (err: any) {
            Swal.fire({ text: err?.response?.data?.message || t('Erro ao confirmar sugestão.'), icon: 'error', timer: 3000, showConfirmButton: false });
        } finally {
            setProcessandoChave(null);
        }
    };

    const handleRecusar = async (chave: string) => {
        setProcessandoChave(chave);
        try {
            await FgtsConciliacaoService.recusarSugestao(esteiraId, chave);
            Swal.fire({ text: t('Sugestão descartada.'), icon: 'success', timer: 2000, showConfirmButton: false });
            await load();
        } catch (err: any) {
            Swal.fire({ text: err?.response?.data?.message || t('Erro ao descartar sugestão.'), icon: 'error', timer: 3000, showConfirmButton: false });
        } finally {
            setProcessandoChave(null);
        }
    };

    return (
        <Modal fullscreen scrollable show={show} onHide={onHide}>
            <div className="modal-content" style={{ backgroundColor: '#F4F5F7' }}>
                <div className="modal-header border-0 pb-1">
                    <div>
                        <h5 className="modal-title fw-bold mb-0" style={{ textTransform: 'uppercase', color: '#5E6397' }}>
                            {t('Análise de Conciliação — FGTS a Recolher')}
                        </h5>
                        <small className="text-muted">{t('Score determinístico por componente (§15 do épico) — sem pontuação de LLM')}</small>
                    </div>
                    <button className="btn btn-icon btn-sm btn-active-light-primary ms-2" onClick={onHide}>
                        <i className="fas fa-times" style={{ color: '#5E6397' }} />
                    </button>
                </div>

                {loading && (
                    <div className="d-flex justify-content-center align-items-center py-5">
                        <span className="spinner-border spinner-border-sm me-2 text-primary" />
                        <span className="text-muted">{t('Carregando...')}</span>
                    </div>
                )}

                {!loading && !relatorio && (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                        <i className="fas fa-circle-exclamation fa-3x mb-3" style={{ color: '#ccc' }} />
                        <p className="mb-0">{t('Não foi possível carregar a análise desta conta.')}</p>
                    </div>
                )}

                {!loading && relatorio && (
                    <>
                        <div className="px-4 pt-3" style={{ backgroundColor: '#fff' }}>
                            <div className="row g-3 pb-3">
                                <div className="col-md-3">
                                    <div className="border rounded p-3 h-100 text-center">
                                        <div className="fw-bold" style={{ fontSize: '1.3rem', color: '#5E6397' }}>{relatorio.quantidadeProvisoes}</div>
                                        <small className="text-muted text-uppercase">{t('Provisões')} · {formatVal(relatorio.valorProvisoes)}</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="border rounded p-3 h-100 text-center">
                                        <div className="fw-bold" style={{ fontSize: '1.3rem', color: '#5E6397' }}>{relatorio.quantidadeBaixas}</div>
                                        <small className="text-muted text-uppercase">{t('Baixas')} · {formatVal(relatorio.valorBaixas)}</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="border rounded p-3 h-100 text-center">
                                        <div className="fw-bold" style={{ fontSize: '1.3rem', color: '#f0ad4e' }}>{relatorio.pendenciasSemCandidato}</div>
                                        <small className="text-muted text-uppercase">{t('Pendências sem candidato')}</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="border rounded p-3 h-100 text-center">
                                        <div className="fw-bold" style={{ fontSize: '1.3rem', color: relatorio.possiveisLancamentosIndevidos.length > 0 ? '#dc3545' : '#28a745' }}>
                                            {relatorio.possiveisLancamentosIndevidos.length}
                                        </div>
                                        <small className="text-muted text-uppercase">{t('Possíveis lançamentos indevidos')}</small>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-4 pb-3">
                                <div className="col-md-6">
                                    <small className="text-muted text-uppercase fw-bold">{t('Distribuição por status')}</small>
                                    <div className="mt-2 d-flex flex-column gap-2">
                                        {relatorio.porStatus.map(s => (
                                            <div key={s.status} className="d-flex align-items-center gap-2">
                                                <span style={{ width: 130, fontSize: '0.8rem', color: STATUS_COR[s.status] ?? '#666' }} className="fw-semibold text-nowrap">
                                                    {s.status}
                                                </span>
                                                <div className="flex-grow-1 bg-light rounded" style={{ height: 10 }}>
                                                    <div style={{ width: `${(s.quantidade / totalPorStatusMax) * 100}%`, height: 10, borderRadius: 5, backgroundColor: STATUS_COR[s.status] ?? '#999' }} />
                                                </div>
                                                <span style={{ width: 150, fontSize: '0.78rem' }} className="text-muted text-nowrap text-end">
                                                    {s.quantidade} · {formatVal(s.valor)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <small className="text-muted text-uppercase fw-bold">{t('Distribuição por modalidade')}</small>
                                    <div className="mt-2 d-flex flex-column gap-2">
                                        {relatorio.porModalidade.map(m => (
                                            <div key={m.modalidade} className="d-flex align-items-center gap-2">
                                                <span style={{ width: 60, fontSize: '0.8rem', color: '#5E6397' }} className="fw-semibold text-nowrap">
                                                    {MODALIDADE_LABEL[m.modalidade] ?? m.modalidade}
                                                </span>
                                                <div className="flex-grow-1 bg-light rounded" style={{ height: 10 }}>
                                                    <div style={{ width: `${(m.quantidade / totalPorModalidadeMax) * 100}%`, height: 10, borderRadius: 5, backgroundColor: '#5E6397' }} />
                                                </div>
                                                <span style={{ width: 40, fontSize: '0.78rem' }} className="text-muted text-nowrap text-end">{m.quantidade}</span>
                                            </div>
                                        ))}
                                        {relatorio.porModalidade.length === 0 && <span className="text-muted" style={{ fontSize: '0.8rem' }}>{t('Nenhum grupo formado ainda.')}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="px-4 pt-2 d-flex gap-2" style={{ backgroundColor: '#fff' }}>
                            <button className={`btn btn-sm ${aba === 'decisoes' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setAba('decisoes')}>
                                {t('Decisões')} <span className="badge bg-secondary ms-1">{decisoes.length}</span>
                            </button>
                            <button className={`btn btn-sm ${aba === 'sugestoes' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setAba('sugestoes')}>
                                {t('Sugestões pendentes')} <span className="badge bg-warning text-dark ms-1">{relatorio.candidatosAguardandoValidacao.length}</span>
                            </button>
                            <button className={`btn btn-sm ${aba === 'eventos' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setAba('eventos')}>
                                {t('Trilha de auditoria')} <span className="badge bg-secondary ms-1">{eventos.length}</span>
                            </button>
                        </div>

                        {aba === 'decisoes' && (
                            <div className="modal-body p-3">
                                {decisoes.length === 0 ? (
                                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                                        <i className="fas fa-list fa-3x mb-3" style={{ color: '#ccc' }} />
                                        <p className="mb-0">{t('Nenhuma decisão registrada ainda.')}</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-hover table-sm align-middle mb-0">
                                            <thead>
                                                <tr style={{ backgroundColor: '#f8f9fa', fontSize: '0.75rem', textTransform: 'uppercase', color: '#6c757d' }}>
                                                    <th className="ps-2">{t('Modalidade')}</th>
                                                    <th>{t('Status funcional')}</th>
                                                    <th className="text-center">{t('Provisões')}</th>
                                                    <th className="text-center">{t('Baixas')}</th>
                                                    <th className="text-center">{t('Divergência')}</th>
                                                    <th className="text-center">{t('Score')}</th>
                                                    <th>{t('Reason codes')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {decisoes.map(d => {
                                                    const expandida = decisaoExpandida === d.id;
                                                    return (
                                                        <Fragment key={d.id}>
                                                            <tr>
                                                                <td
                                                                    className="ps-2 text-nowrap"
                                                                    style={{ fontSize: '0.85rem', cursor: 'pointer', color: '#5E6397', fontWeight: 600 }}
                                                                    onClick={() => setDecisaoExpandida(expandida ? null : d.id)}
                                                                    title={t('Ver score por componente')}
                                                                >
                                                                    <i className={`fas ${expandida ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`} style={{ fontSize: '0.65rem' }} />
                                                                    {MODALIDADE_LABEL[d.modalidade] ?? d.modalidade}
                                                                </td>
                                                                <td style={{ fontSize: '0.82rem' }}>{d.statusFuncional.replace('CONCILIADO_', '')}</td>
                                                                <td className="text-center" style={{ fontSize: '0.85rem' }}>{d.quantidadeProvisoes}</td>
                                                                <td className="text-center" style={{ fontSize: '0.85rem' }}>{d.quantidadeBaixas}</td>
                                                                <td className="text-center fw-semibold" style={{ fontSize: '0.85rem', color: d.percentualDivergencia == null ? '#999' : (d.percentualDivergencia >= 0 ? '#0d6efd' : '#dc3545') }}>
                                                                    {formatPct(d.percentualDivergencia)}
                                                                </td>
                                                                <td className="text-center">
                                                                    <span className={`badge ${FAIXA_SCORE_COR[d.faixaScore] ?? 'bg-secondary'}`} style={{ fontSize: '0.7rem' }} title={d.faixaScore}>
                                                                        {d.scoreTotal.toFixed(0)}
                                                                    </span>
                                                                </td>
                                                                <td style={{ fontSize: '0.72rem' }}>
                                                                    {d.reasonCodes.map(rc => (
                                                                        <span key={rc} className="badge bg-light text-dark border me-1 mb-1" style={{ fontSize: '0.65rem' }}>
                                                                            {rc.replace(/_/g, ' ')}
                                                                        </span>
                                                                    ))}
                                                                </td>
                                                            </tr>
                                                            {expandida && (
                                                                <tr>
                                                                    <td colSpan={8} className="p-0" style={{ backgroundColor: '#F4F5F7' }}>
                                                                        <div className="p-3">
                                                                            <small className="text-muted text-uppercase fw-bold d-block mb-2">
                                                                                <i className="fas fa-chart-simple me-1" />
                                                                                {t('Score por componente')} (§34 {t('do épico')})
                                                                            </small>
                                                                            <div className="row g-2">
                                                                                {componentesScore(d).map(c => (
                                                                                    <div key={c.label} className="col-md-2 col-sm-4">
                                                                                        <div className="border rounded p-2 bg-white h-100">
                                                                                            <div className="fw-semibold mb-1" style={{ fontSize: '0.78rem' }}>{t(c.label)}</div>
                                                                                            <div className="bg-light rounded mb-1" style={{ height: 6 }}>
                                                                                                <div style={{ width: `${c.max > 0 ? (c.valor / c.max) * 100 : 0}%`, height: 6, borderRadius: 3, backgroundColor: '#5E6397' }} />
                                                                                            </div>
                                                                                            <div className="text-muted" style={{ fontSize: '0.72rem' }}>{c.valor}/{c.max}</div>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        {aba === 'sugestoes' && (
                            <div className="modal-body p-3">
                                {relatorio.candidatosAguardandoValidacao.length === 0 ? (
                                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                                        <i className="fas fa-check-double fa-3x mb-3" style={{ color: '#ccc' }} />
                                        <p className="mb-0">{t('Nenhuma sugestão aguardando confirmação.')}</p>
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column gap-2">
                                        {relatorio.candidatosAguardandoValidacao.map(s => (
                                            <div key={s.chaveVinculoMatch} className="border rounded p-3 bg-white">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div>
                                                        <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{s.justificativa ?? t('Sugestão do motor')}</div>
                                                        <small className="text-muted">{s.origemMatch}</small>
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            className="btn btn-sm btn-success"
                                                            disabled={processandoChave === s.chaveVinculoMatch}
                                                            onClick={() => handleAceitar(s.chaveVinculoMatch)}
                                                        >
                                                            <i className="fas fa-check me-1" />{t('Aceitar')}
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            disabled={processandoChave === s.chaveVinculoMatch}
                                                            onClick={() => handleRecusar(s.chaveVinculoMatch)}
                                                        >
                                                            <i className="fas fa-xmark me-1" />{t('Recusar')}
                                                        </button>
                                                    </div>
                                                </div>
                                                <table className="table table-sm mb-0">
                                                    <thead>
                                                        <tr style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: '#6c757d' }}>
                                                            <th>{t('Tipo')}</th>
                                                            <th>{t('Valor')}</th>
                                                            <th>{t('Data')}</th>
                                                            <th>{t('Histórico')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {s.lancamentos.map(l => (
                                                            <tr key={l.id} style={{ fontSize: '0.78rem' }}>
                                                                <td>{l.tipoLancamento === 'C' ? t('Provisão') : t('Baixa')}</td>
                                                                <td>{formatVal(l.valor)}</td>
                                                                <td>{moment(l.data).format('DD/MM/YYYY')}</td>
                                                                <td>{l.historico}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {aba === 'eventos' && (
                            <div className="modal-body p-3">
                                {eventos.length === 0 ? (
                                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                                        <i className="fas fa-clock-rotate-left fa-3x mb-3" style={{ color: '#ccc' }} />
                                        <p className="mb-0">{t('Nenhum evento de auditoria registrado ainda.')}</p>
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column gap-2">
                                        {eventos.map(e => (
                                            <div key={e.id} className="d-flex align-items-start gap-2 border-bottom pb-2">
                                                <i className={`fas ${EVENTO_ICONE[e.tipoEvento] ?? 'fa-circle-info'} mt-1`} style={{ color: '#5E6397', width: 16 }} />
                                                <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="fw-semibold" style={{ fontSize: '0.82rem' }}>{e.tipoEvento.replace(/_/g, ' ')}</span>
                                                        <span className="text-muted" style={{ fontSize: '0.72rem' }}>{moment(e.dataCriacao).format('DD/MM/YYYY HH:mm')}</span>
                                                    </div>
                                                    <div className="text-muted" style={{ fontSize: '0.78rem', wordBreak: 'break-word' }}>
                                                        {formatDetalhe(e.detalhe)}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};

export default ModalFgtsAnalise;
